(() => {
  'use strict';

  const IS_COMMONJS_TEST = typeof module === 'object' && module !== null && module.exports;
  const CONSENT_STATE_API_KEY = 'NOUVACConsent';
  const UI_CHOICES = Object.freeze(['unknown', 'accepted', 'refused']);
  const TECH_CONSENT_STATES = Object.freeze(['granted', 'denied']);

  function normalizeShopifyConsent(value) {
    if (value === 'yes' || value === true) return true;
    if (value === 'no' || value === false) return false;
    return undefined;
  }

  function readShopifyMarketingConsent(api) {
    try {
      return normalizeShopifyConsent(api?.currentVisitorConsent?.().marketing);
    } catch (_) {
      return undefined;
    }
  }

  function getSitewideEngagementDetail(event, baseUrl) {
    if (!event?.isTrusted || event.button !== 0 || typeof event.target?.closest !== 'function') return null;
    if (event.target.closest('nouvac-cookie-consent')) return null;

    const control = event.target.closest('a[href], button');
    if (!control || control.disabled || control.getAttribute('aria-disabled') === 'true') return null;
    if (!control.matches('a[href]')) return 'sitewide_button';

    try {
      const destination = new URL(control.getAttribute('href'), baseUrl);
      const currentLocation = new URL(baseUrl);
      if (!['http:', 'https:'].includes(destination.protocol)) return null;
      return destination.origin === currentLocation.origin ? 'sitewide_internal_link' : null;
    } catch (_) {
      return null;
    }
  }

  function isBookingTunnelDocument(documentObject) {
    return documentObject?.documentElement?.dataset?.nouvacBookingTunnel === 'true';
  }

  function createBookingSilentAcceptController({
    getApi,
    performMarketingAccept
  } = {}) {
    let inFlight = null;
    let acceptAttempted = false;
    let done = false;

    const run = () => {
      if (done) return Promise.resolve(false);
      if (inFlight) return inFlight;
      if (acceptAttempted) return Promise.resolve(false);
      if (typeof getApi !== 'function' || typeof performMarketingAccept !== 'function') {
        return Promise.resolve(false);
      }

      inFlight = Promise.resolve()
        .then(() => getApi())
        .then((api) => {
          if (readShopifyMarketingConsent(api) === true) {
            done = true;
            return false;
          }

          // Booking entry intentionally overrides an explicit prior refusal through Shopify's real Accept path.
          acceptAttempted = true;
          return Promise.resolve(performMarketingAccept(api))
            .then((succeeded) => {
              if (readShopifyMarketingConsent(api) !== true) return false;
              done = true;
              return succeeded === true;
            });
        })
        .catch(() => false)
        .finally(() => {
          inFlight = null;
        });

      return inFlight;
    };

    return Object.freeze({
      run,
      hasAttemptedAccept: () => acceptAttempted,
      isDone: () => done
    });
  }

  function createConsentStateController({
    getTimestamp = () => new Date().toISOString(),
    logger = null,
    shopifyApi = null
  } = {}) {
    let customerPrivacyApi = null;
    let uiChoice = 'unknown';
    let observedTechConsent = 'denied';
    let marketingAcceptHandler = null;
    let marketingAcceptPromise = null;
    const transitions = [];

    const getMarketingConsent = () => readShopifyMarketingConsent(customerPrivacyApi);
    const getUiChoice = () => {
      const marketingConsent = getMarketingConsent();
      if (marketingConsent === true) return 'accepted';
      if (marketingConsent === false) return 'refused';
      return uiChoice;
    };
    const getTechConsent = () => (getMarketingConsent() === true ? 'granted' : 'denied');

    const logTransition = (reason, detail = '') => {
      const entry = {
        ts: getTimestamp(),
        ui_choice: getUiChoice(),
        tech_consent: getTechConsent(),
        reason
      };
      if (detail) entry.detail = detail;
      Object.freeze(entry);
      transitions.push(entry);
      if (logger && typeof logger.info === 'function') {
        logger.info('[NOUVAC Consent][Shopify Customer Privacy]', entry);
      }
    };

    const syncFromShopify = (reason = 'shopify_sync', detail = '', forceLog = false) => {
      const marketingConsent = getMarketingConsent();
      const nextUiChoice = marketingConsent === true
        ? 'accepted'
        : (marketingConsent === false ? 'refused' : 'unknown');
      const nextTechConsent = marketingConsent === true ? 'granted' : 'denied';
      if (!UI_CHOICES.includes(nextUiChoice) || !TECH_CONSENT_STATES.includes(nextTechConsent)) return false;

      const changed = uiChoice !== nextUiChoice || observedTechConsent !== nextTechConsent;
      uiChoice = nextUiChoice;
      observedTechConsent = nextTechConsent;
      if (!changed && !forceLog) return false;
      logTransition(reason, detail);
      return true;
    };

    const attachShopifyApi = (api, reason = 'hydrate') => {
      customerPrivacyApi = api
        && typeof api.currentVisitorConsent === 'function'
        && typeof api.setTrackingConsent === 'function'
        ? api
        : null;
      return syncFromShopify(reason);
    };

    const setMarketingAcceptHandler = (handler) => {
      marketingAcceptHandler = typeof handler === 'function' ? handler : null;
    };

    const maybeEngagementRegrant = (detail = 'sitewide_engagement') => {
      if (getMarketingConsent() === true || typeof marketingAcceptHandler !== 'function') {
        return Promise.resolve(false);
      }
      if (marketingAcceptPromise) return marketingAcceptPromise;

      const transitionDetail = typeof detail === 'string' && detail ? detail : 'sitewide_engagement';
      let marketingAcceptResult;
      try {
        marketingAcceptResult = marketingAcceptHandler(transitionDetail);
      } catch (_) {
        return Promise.resolve(false);
      }
      marketingAcceptPromise = Promise.resolve(marketingAcceptResult)
        .then((succeeded) => succeeded === true && getMarketingConsent() === true)
        .catch(() => false)
        .finally(() => {
          marketingAcceptPromise = null;
        });
      return marketingAcceptPromise;
    };

    const api = Object.freeze({
      getUiChoice,
      getTechConsent,
      isMarketingAllowed: () => getMarketingConsent() === true,
      getTransitionLog: () => Object.freeze(transitions.slice()),
      maybeEngagementRegrant
    });

    logTransition('init');
    if (shopifyApi) attachShopifyApi(shopifyApi);

    return Object.freeze({
      api,
      attachShopifyApi,
      setMarketingAcceptHandler,
      syncFromShopify
    });
  }

  if (IS_COMMONJS_TEST) {
    module.exports = Object.freeze({
      createBookingSilentAcceptController,
      createConsentStateController,
      getSitewideEngagementDetail,
      isBookingTunnelDocument,
      normalizeShopifyConsent,
      readShopifyMarketingConsent
    });
    return;
  }

  if (customElements.get('nouvac-cookie-consent')) return;

  const IS_BOOKING_TUNNEL = isBookingTunnelDocument(document);

  const consentStateController = createConsentStateController({ logger: window.console });
  Object.defineProperty(window, CONSENT_STATE_API_KEY, {
    configurable: false,
    enumerable: true,
    value: consentStateController.api,
    writable: false
  });
  document.addEventListener('click', (event) => {
    const detail = getSitewideEngagementDetail(event, window.location.href);
    if (detail) void consentStateController.api.maybeEngagementRegrant(detail);
  }, { capture: true });

  const CONTROLLER_KEY = '__nouvacCookieConsentController';
  const PURPOSES = Object.freeze(['analytics', 'marketing', 'preferences']);
  const RUNTIME_MODES = Object.freeze({
    SHOPIFY: 'SHOPIFY',
    LOCAL_FALLBACK: 'LOCAL_FALLBACK'
  });
  const LOCAL_FALLBACK_STORAGE_KEY = 'nouvac_cookie_consent_fallback_v1';
  const LOCAL_FALLBACK_SCHEMA_VERSION = 1;
  const LOCAL_FALLBACK_RETENTION_MS = 180 * 24 * 60 * 60 * 1000;
  const LOCAL_FALLBACK_CLOCK_SKEW_MS = 5 * 60 * 1000;
  const LOCAL_FALLBACK_DECISIONS = Object.freeze(['accept', 'refuse', 'custom']);
  const LOCAL_FALLBACK_FIELDS = Object.freeze([
    'version',
    'decision',
    'categories',
    'createdAt',
    'expiresAt',
    'pendingShopifySync'
  ]);
  const SHOPIFY_WAIT_ATTEMPTS = 80;
  const SHOPIFY_WAIT_INTERVAL = 100;
  const FEATURE_LOAD_TIMEOUT = 3000;
  const FEATURE_RETRY_DELAYS = [0, 250, 750];
  const PRIVACY_API_WAIT_ATTEMPTS = 20;
  const VIEW_META = {
    consent: {
      title: 'NouvacCookieConsentTitle',
      description: 'NouvacCookieConsentDescription'
    },
    preferences: {
      title: 'NouvacCookiePreferencesTitle',
      description: 'NouvacCookiePreferencesDescription'
    }
  };

  class NouvacCookieConsent extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      if (window[CONTROLLER_KEY] && window[CONTROLLER_KEY] !== this) {
        this.remove();
        return;
      }

      window[CONTROLLER_KEY] = this;
      this.initialized = true;
      this.api = null;
      this.apiPromise = null;
      this.mode = null;
      this.initialDisplayEvaluated = false;
      this.sessionResolved = false;
      this.isOpen = false;
      this.isSaving = false;
      this.isSynchronizing = false;
      this.activeView = 'consent';
      this.retryMode = null;
      this.lastSubmission = null;
      this.marketingAcceptPromise = null;
      this.initializationRun = 0;
      this.initializationTimers = new Map();
      this.consentOperationRun = 0;
      this.consentOperationTimers = new Map();
      this.abortController = new AbortController();
      this.isSilentBookingTunnel = IS_BOOKING_TUNNEL;
      this.dialog = null;
      this.retryButton = null;
      this.status = null;
      this.panels = [];
      this.toggles = [];
      this.actionButtons = [];
      this.viewButtons = [];

      if (this.isSilentBookingTunnel) {
        this.hidden = true;
        this.setAttribute('aria-hidden', 'true');
        this.silentBookingAcceptController = createBookingSilentAcceptController({
          getApi: () => this.getApi(),
          performMarketingAccept: () => this.performShopifyMarketingAccept({
            automatic: true,
            reason: 'booking_tunnel_auto_accept'
          })
        });
        this.marketingAcceptHandler = () => this.silentBookingAcceptController.run();
        consentStateController.setMarketingAcceptHandler(this.marketingAcceptHandler);
        void this.silentBookingAcceptController.run();
        return;
      }

      this.marketingAcceptHandler = (detail) => this.performShopifyMarketingAccept({
        automatic: true,
        reason: 'engagement_regrant',
        detail
      });
      consentStateController.setMarketingAcceptHandler(this.marketingAcceptHandler);

      this.dialog = this.querySelector('.nouvac-cookie-consent__dialog');
      this.retryButton = this.querySelector('[data-nouvac-cookie-retry]');
      this.status = this.querySelector('[data-nouvac-cookie-status]');
      this.panels = Array.from(this.querySelectorAll('[data-nouvac-cookie-panel]'));
      this.toggles = Array.from(this.querySelectorAll('[data-nouvac-cookie-purpose]'));
      this.actionButtons = Array.from(this.querySelectorAll('[data-nouvac-cookie-action]'));
      this.viewButtons = Array.from(this.querySelectorAll('[data-nouvac-cookie-open-view]'));

      const signal = this.abortController.signal;
      this.addEventListener('click', this.handleClick.bind(this), { signal });
      document.addEventListener('visitorConsentCollected', this.handleConsentEvent.bind(this), { signal });
      this.toggles.forEach((toggle) => {
        toggle.addEventListener('change', () => {
          toggle.dataset.consentState = toggle.checked ? 'yes' : 'no';
        }, { signal });
      });

      this.startInitialization();
    }

    disconnectedCallback() {
      this.initializationRun += 1;
      this.clearInitializationTimers();
      this.cancelConsentOperation();
      this.abortController?.abort();
      if (this.marketingAcceptHandler) consentStateController.setMarketingAcceptHandler(null);
      if (window[CONTROLLER_KEY] === this) delete window[CONTROLLER_KEY];
      this.initialized = false;
    }

    startInitialization() {
      const run = this.initializationRun + 1;
      this.initializationRun = run;
      this.clearInitializationTimers();
      this.cancelConsentOperation();
      this.api = null;
      this.apiPromise = null;
      this.mode = null;
      this.initialDisplayEvaluated = false;
      this.sessionResolved = false;
      this.isSaving = false;
      this.isSynchronizing = false;
      this.dialog.setAttribute('aria-busy', 'false');
      this.retryButton.disabled = true;
      this.setInteractive(false);
      this.clearStatus();

      this.getApi(run)
        .then(async (api) => {
          if (!this.isCurrentInitialization(run)) return;
          await this.evaluateInitialDisplay(api, run);
        })
        .catch(() => {
          if (!this.isCurrentInitialization(run)) return;
          this.handleApiFailure();
        })
        .finally(() => {
          if (this.isCurrentInitialization(run)) this.retryButton.disabled = false;
        });
    }

    isCurrentInitialization(run) {
      return this.isConnected && window[CONTROLLER_KEY] === this && this.initializationRun === run;
    }

    clearInitializationTimers() {
      const cancellations = Array.from(this.initializationTimers.values());
      this.initializationTimers.clear();
      cancellations.forEach((cancel) => cancel());
    }

    waitForInitialization(delay, run) {
      return new Promise((resolve, reject) => {
        let settled = false;
        let timer;

        const finish = (callback, value) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          this.initializationTimers.delete(timer);
          callback(value);
        };

        timer = window.setTimeout(() => {
          if (!this.isCurrentInitialization(run)) {
            finish(reject, new Error('Consent initialization cancelled'));
            return;
          }
          finish(resolve);
        }, delay);

        this.initializationTimers.set(timer, () => {
          finish(reject, new Error('Consent initialization cancelled'));
        });
      });
    }

    cancelConsentOperation() {
      this.consentOperationRun += 1;
      const cancellations = Array.from(this.consentOperationTimers.values());
      this.consentOperationTimers.clear();
      cancellations.forEach((cancel) => cancel());
    }

    startConsentOperation() {
      this.cancelConsentOperation();
      return this.consentOperationRun;
    }

    finishConsentOperation(run) {
      if (this.consentOperationRun !== run) return;
      const cancellations = Array.from(this.consentOperationTimers.values());
      this.consentOperationTimers.clear();
      cancellations.forEach((cancel) => cancel());
    }

    isCurrentConsentOperation(run) {
      return this.isConnected && window[CONTROLLER_KEY] === this && this.consentOperationRun === run;
    }

    waitForConsentOperation(delay, run) {
      return new Promise((resolve, reject) => {
        let settled = false;
        let timer;

        const finish = (callback, value) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          this.consentOperationTimers.delete(timer);
          callback(value);
        };

        timer = window.setTimeout(() => {
          if (!this.isCurrentConsentOperation(run)) {
            finish(reject, new Error('Consent operation cancelled'));
            return;
          }
          finish(resolve);
        }, delay);

        this.consentOperationTimers.set(timer, () => {
          finish(reject, new Error('Consent operation cancelled'));
        });
      });
    }

    hasUsableCustomerPrivacyApi(api) {
      return Boolean(
        api
        && typeof api.currentVisitorConsent === 'function'
        && typeof api.shouldShowBanner === 'function'
        && typeof api.setTrackingConsent === 'function'
      );
    }

    async waitForShopify(run) {
      for (let attempt = 0; attempt < SHOPIFY_WAIT_ATTEMPTS; attempt += 1) {
        if (!this.isCurrentInitialization(run)) throw new Error('Consent initialization cancelled');
        if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') return;
        await this.waitForInitialization(SHOPIFY_WAIT_INTERVAL, run);
      }
      throw new Error('Shopify loadFeatures unavailable');
    }

    loadFeatureOnce(run) {
      return new Promise((resolve, reject) => {
        let settled = false;
        let timer;

        const finish = (callback, value) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          this.initializationTimers.delete(timer);
          callback(value);
        };

        timer = window.setTimeout(() => {
          finish(reject, new Error('Customer Privacy feature load timeout'));
        }, FEATURE_LOAD_TIMEOUT);

        this.initializationTimers.set(timer, () => {
          finish(reject, new Error('Consent initialization cancelled'));
        });

        try {
          window.Shopify.loadFeatures(
            [
              {
                name: 'consent-tracking-api',
                version: '0.1'
              }
            ],
            (error) => {
              if (!this.isCurrentInitialization(run)) {
                finish(reject, new Error('Consent initialization cancelled'));
                return;
              }
              if (this.hasUsableCustomerPrivacyApi(window.Shopify?.customerPrivacy)) {
                finish(resolve);
                return;
              }
              if (error) {
                finish(reject, error);
                return;
              }
              finish(resolve);
            }
          );
        } catch (error) {
          finish(reject, error);
        }
      });
    }

    async waitForCustomerPrivacy(run) {
      for (let attempt = 0; attempt < PRIVACY_API_WAIT_ATTEMPTS; attempt += 1) {
        if (!this.isCurrentInitialization(run)) throw new Error('Consent initialization cancelled');
        if (this.hasUsableCustomerPrivacyApi(window.Shopify?.customerPrivacy)) {
          return window.Shopify.customerPrivacy;
        }
        await this.waitForInitialization(SHOPIFY_WAIT_INTERVAL, run);
      }
      throw new Error('Customer Privacy API unavailable');
    }

    async loadCustomerPrivacy(run) {
      await this.waitForShopify(run);
      if (this.hasUsableCustomerPrivacyApi(window.Shopify?.customerPrivacy)) {
        return window.Shopify.customerPrivacy;
      }

      let lastError = new Error('Customer Privacy API unavailable');
      for (const delay of FEATURE_RETRY_DELAYS) {
        if (delay) await this.waitForInitialization(delay, run);
        if (this.hasUsableCustomerPrivacyApi(window.Shopify?.customerPrivacy)) {
          return window.Shopify.customerPrivacy;
        }
        try {
          await this.loadFeatureOnce(run);
          return await this.waitForCustomerPrivacy(run);
        } catch (error) {
          if (!this.isCurrentInitialization(run)) throw error;
          lastError = error;
        }
      }
      throw lastError;
    }

    getApi(run = this.initializationRun) {
      if (this.api) return Promise.resolve(this.api);
      if (this.apiPromise) return this.apiPromise;

      this.apiPromise = this.loadCustomerPrivacy(run)
        .then((api) => {
          if (!this.isCurrentInitialization(run)) throw new Error('Consent initialization cancelled');
          this.api = api;
          consentStateController.attachShopifyApi(api);
          return api;
        })
        .catch((error) => {
          if (this.initializationRun === run) this.apiPromise = null;
          throw error;
        });

      return this.apiPromise;
    }

    hasExactKeys(value, expectedKeys) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
      const keys = Object.keys(value);
      return keys.length === expectedKeys.length && expectedKeys.every((key) => keys.includes(key));
    }

    hasValidCategories(categories) {
      if (!this.hasExactKeys(categories, PURPOSES)) return false;
      return PURPOSES.every((purpose) => typeof categories[purpose] === 'boolean');
    }

    isValidLocalFallbackConsent(value, now = Date.now()) {
      if (!this.hasExactKeys(value, LOCAL_FALLBACK_FIELDS)) return false;
      if (value.version !== LOCAL_FALLBACK_SCHEMA_VERSION) return false;
      if (!LOCAL_FALLBACK_DECISIONS.includes(value.decision)) return false;
      if (!this.hasValidCategories(value.categories)) return false;
      if (value.pendingShopifySync !== true) return false;
      if (!Number.isSafeInteger(value.createdAt) || !Number.isSafeInteger(value.expiresAt)) return false;
      if (value.createdAt <= 0 || value.expiresAt <= value.createdAt) return false;
      if (value.createdAt > now + LOCAL_FALLBACK_CLOCK_SKEW_MS) return false;
      if (value.expiresAt <= now) return false;
      if (value.expiresAt - value.createdAt !== LOCAL_FALLBACK_RETENTION_MS) return false;
      if (value.decision === 'accept' && !PURPOSES.every((purpose) => value.categories[purpose])) return false;
      if (value.decision === 'refuse' && !PURPOSES.every((purpose) => !value.categories[purpose])) return false;
      return true;
    }

    normalizeLocalFallbackConsent(value) {
      const categories = Object.freeze(PURPOSES.reduce((normalized, purpose) => {
        normalized[purpose] = value.categories[purpose];
        return normalized;
      }, {}));

      return Object.freeze({
        version: LOCAL_FALLBACK_SCHEMA_VERSION,
        decision: value.decision,
        categories,
        createdAt: value.createdAt,
        expiresAt: value.expiresAt,
        pendingShopifySync: true
      });
    }

    readLocalFallbackConsent() {
      let storedValue;
      try {
        storedValue = window.localStorage.getItem(LOCAL_FALLBACK_STORAGE_KEY);
      } catch (_) {
        return null;
      }

      if (storedValue === null) return null;

      let parsedValue;
      try {
        parsedValue = JSON.parse(storedValue);
      } catch (_) {
        this.removeLocalFallbackConsent();
        return null;
      }

      if (!this.isValidLocalFallbackConsent(parsedValue)) {
        this.removeLocalFallbackConsent();
        return null;
      }

      return this.normalizeLocalFallbackConsent(parsedValue);
    }

    writeLocalFallbackConsent(decision, categories) {
      const createdAt = Date.now();
      const value = {
        version: LOCAL_FALLBACK_SCHEMA_VERSION,
        decision,
        categories: PURPOSES.reduce((normalized, purpose) => {
          normalized[purpose] = categories[purpose];
          return normalized;
        }, {}),
        createdAt,
        expiresAt: createdAt + LOCAL_FALLBACK_RETENTION_MS,
        pendingShopifySync: true
      };

      if (!this.isValidLocalFallbackConsent(value, createdAt)) return null;

      try {
        window.localStorage.setItem(LOCAL_FALLBACK_STORAGE_KEY, JSON.stringify(value));
      } catch (_) {
        return null;
      }

      const persistedValue = this.readLocalFallbackConsent();
      if (!persistedValue) return null;
      if (persistedValue.decision !== value.decision) return null;
      if (persistedValue.createdAt !== value.createdAt || persistedValue.expiresAt !== value.expiresAt) return null;
      if (!PURPOSES.every((purpose) => persistedValue.categories[purpose] === value.categories[purpose])) return null;
      return persistedValue;
    }

    removeLocalFallbackConsent() {
      try {
        window.localStorage.removeItem(LOCAL_FALLBACK_STORAGE_KEY);
        return window.localStorage.getItem(LOCAL_FALLBACK_STORAGE_KEY) === null;
      } catch (_) {
        return false;
      }
    }

    handleApiFailure() {
      if (!this.isConnected || window[CONTROLLER_KEY] !== this) return;
      this.api = null;
      this.apiPromise = null;
      consentStateController.attachShopifyApi(null, 'shopify_unavailable');
      this.mode = RUNTIME_MODES.LOCAL_FALLBACK;
      this.initialDisplayEvaluated = true;
      this.clearStatus();
      this.setInteractive(true);

      const fallback = this.readLocalFallbackConsent();
      if (fallback) {
        this.sessionResolved = true;
        this.syncPreferencesFromCategories(fallback.categories);
        if (this.isOpen) this.close();
        return;
      }

      this.sessionResolved = false;
      this.syncPreferencesFromCategories(null);
      this.open('consent');
    }

    handleClick(event) {
      const viewTrigger = event.target.closest('[data-nouvac-cookie-open-view]');
      if (viewTrigger) {
        this.setView(viewTrigger.dataset.nouvacCookieOpenView, true);
        return;
      }

      if (event.target.closest('[data-nouvac-cookie-retry]')) {
        this.retry();
        return;
      }

      const action = event.target.closest('[data-nouvac-cookie-action]');
      if (action) {
        this.submit(action.dataset.nouvacCookieAction);
        return;
      }

    }

    handleConsentEvent() {
      if (
        this.mode !== RUNTIME_MODES.SHOPIFY
        || !this.hasUsableCustomerPrivacyApi(this.api)
        || this.isSaving
        || this.isSynchronizing
      ) return;

      try {
        const consent = this.api.currentVisitorConsent();
        consentStateController.syncFromShopify('shopify_update');
        this.syncPreferencesFromCategories(consent);
        if (this.hasCompleteExplicitConsent(consent)) {
          this.removeLocalFallbackConsent();
          this.sessionResolved = true;
          this.initialDisplayEvaluated = true;
          if (this.isOpen) this.close();
        }
      } catch (_) {
        // Shopify remains authoritative; a later initialization can retry safely.
      }
    }

    normalizeConsent(value) {
      return normalizeShopifyConsent(value);
    }

    hasCompleteExplicitConsent(consent) {
      return PURPOSES.every((purpose) => this.normalizeConsent(consent?.[purpose]) !== undefined);
    }

    async evaluateInitialDisplay(api, run) {
      if (!this.isCurrentInitialization(run) || this.initialDisplayEvaluated || this.sessionResolved) return;
      if (!this.hasUsableCustomerPrivacyApi(api)) throw new Error('Customer Privacy API unusable');

      this.mode = RUNTIME_MODES.SHOPIFY;
      const consent = api.currentVisitorConsent();

      if (this.hasCompleteExplicitConsent(consent)) {
        this.removeLocalFallbackConsent();
        this.applyShopifyDisplay(api, consent);
        return;
      }

      const fallback = this.readLocalFallbackConsent();
      if (fallback?.pendingShopifySync) {
        await this.synchronizePendingFallback(api, fallback, run);
        return;
      }

      this.applyShopifyDisplay(api, consent);
    }

    applyShopifyDisplay(api, consent, shouldShow = api.shouldShowBanner() === true) {
      consentStateController.attachShopifyApi(api);
      const hasExplicitDecision = this.hasCompleteExplicitConsent(consent);
      this.syncPreferencesFromCategories(consent);
      this.initialDisplayEvaluated = true;
      this.sessionResolved = hasExplicitDecision;
      this.setInteractive(true);
      this.clearStatus();

      if (hasExplicitDecision) {
        if (this.isOpen) this.close();
        return;
      }

      if (shouldShow) {
        this.open('consent');
      } else if (this.isOpen) {
        this.close();
      }
    }

    async synchronizePendingFallback(api, fallback, run) {
      if (!this.isCurrentInitialization(run) || this.isSaving || this.isSynchronizing) return;

      this.isSynchronizing = true;
      this.initialDisplayEvaluated = true;
      this.sessionResolved = true;
      this.syncPreferencesFromCategories(fallback.categories);
      this.clearStatus();
      this.setInteractive(false);
      if (this.isOpen) this.close();
      const operation = this.startConsentOperation();

      try {
        await this.recordConsent(api, fallback.categories, operation);
        if (!this.isCurrentInitialization(run) || !this.isCurrentConsentOperation(operation)) {
          throw new Error('Consent synchronization cancelled');
        }

        const consent = api.currentVisitorConsent();
        if (!this.hasCompleteExplicitConsent(consent)) {
          throw new Error('Shopify consent remained incomplete');
        }
        const shouldShow = api.shouldShowBanner() === true;

        this.removeLocalFallbackConsent();
        this.applyShopifyDisplay(api, consent, shouldShow);
      } catch (_) {
        if (this.isCurrentInitialization(run)) {
          this.initialDisplayEvaluated = true;
          this.sessionResolved = true;
          this.syncPreferencesFromCategories(fallback.categories);
          this.clearStatus();
          this.setInteractive(true);
          if (this.isOpen) this.close();
        }
      } finally {
        this.finishConsentOperation(operation);
        if (this.isCurrentInitialization(run)) this.isSynchronizing = false;
      }
    }

    syncPreferencesFromCategories(categories) {
      this.toggles.forEach((toggle) => {
        const value = this.normalizeConsent(categories?.[toggle.dataset.nouvacCookiePurpose]);
        toggle.checked = value === true;
        toggle.indeterminate = false;
        toggle.dataset.consentState = value === undefined ? 'unset' : (value ? 'yes' : 'no');
      });
    }

    refreshPreferencesForReopen() {
      if (this.mode === RUNTIME_MODES.LOCAL_FALLBACK) {
        const fallback = this.readLocalFallbackConsent();
        this.syncPreferencesFromCategories(fallback?.categories);
        return;
      }

      if (this.mode !== RUNTIME_MODES.SHOPIFY || !this.hasUsableCustomerPrivacyApi(this.api)) return;

      try {
        const consent = this.api.currentVisitorConsent();
        if (this.hasCompleteExplicitConsent(consent)) {
          this.removeLocalFallbackConsent();
          this.syncPreferencesFromCategories(consent);
          return;
        }

        const fallback = this.readLocalFallbackConsent();
        this.syncPreferencesFromCategories(fallback?.categories || consent);
      } catch (_) {
        const fallback = this.readLocalFallbackConsent();
        if (fallback) this.syncPreferencesFromCategories(fallback.categories);
      }
    }

    setView(view, focusHeading = false) {
      if (!VIEW_META[view]) return;
      this.activeView = view;
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.nouvacCookiePanel !== view;
      });
      this.dataset.nouvacCookieView = view;
      this.dialog.setAttribute('aria-labelledby', VIEW_META[view].title);
      this.dialog.setAttribute('aria-describedby', VIEW_META[view].description);

      if (focusHeading) {
        const heading = this.querySelector(`#${VIEW_META[view].title}`);
        window.requestAnimationFrame(() => {
          if (this.isConnected) heading?.focus({ preventScroll: true });
        });
      }
    }

    open(view) {
      if (!this.isOpen && view === 'preferences') this.refreshPreferencesForReopen();
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      this.isOpen = true;
      this.setView(view, false);
    }

    close() {
      if (this.isSaving) return;
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      this.isOpen = false;
      this.clearStatus();
    }

    buildPayload(action) {
      if (action === 'accept') {
        return { analytics: true, marketing: true, preferences: true };
      }
      if (action === 'reject') {
        return { analytics: false, marketing: false, preferences: false };
      }

      return PURPOSES.reduce((payload, purpose) => {
        const toggle = this.toggles.find((input) => input.dataset.nouvacCookiePurpose === purpose);
        payload[purpose] = Boolean(toggle?.checked);
        return payload;
      }, {});
    }

    submit(action) {
      if (this.isSaving || this.isSynchronizing || !this.isOpen) return;
      if (!['accept', 'reject', 'save'].includes(action)) return;
      const decision = action === 'accept' ? 'accept' : (action === 'reject' ? 'refuse' : 'custom');
      const payload = this.buildPayload(action);
      const transitionReason = this.getShopifyTransitionReason(decision, payload.marketing);
      if (action === 'accept') {
        void this.performShopifyMarketingAccept({ reason: transitionReason });
        return;
      }
      void this.submitPayload(payload, decision, { transitionReason });
    }

    getShopifyTransitionReason(decision, marketingAllowed) {
      if (decision === 'refuse' || marketingAllowed !== true) return 'refuse';
      return consentStateController.api.getUiChoice() === 'refused' ? 'accept_after_refuse' : 'accept';
    }

    performShopifyMarketingAccept({
      automatic = false,
      reason = 'accept',
      detail = ''
    } = {}) {
      if (automatic && (this.isSaving || this.isSynchronizing)) return Promise.resolve(false);
      if (automatic && consentStateController.api.isMarketingAllowed()) return Promise.resolve(false);
      if (this.marketingAcceptPromise) return this.marketingAcceptPromise;

      // Marketing authority = Shopify Customer Privacy; auto path reuses Accept.
      const payload = this.buildPayload('accept');
      const run = async () => {
        let apiOverride = null;
        if (automatic) {
          apiOverride = this.hasUsableCustomerPrivacyApi(this.api) ? this.api : null;
          if (!apiOverride) {
            try {
              apiOverride = await this.getApi();
            } catch (_) {
              return false;
            }
          }
          if (!this.hasUsableCustomerPrivacyApi(apiOverride)) return false;
          consentStateController.attachShopifyApi(apiOverride);
          if (consentStateController.api.isMarketingAllowed()) return false;
        }

        return this.submitPayload(payload, 'accept', {
          automatic,
          apiOverride,
          transitionReason: reason,
          transitionDetail: detail
        });
      };

      this.marketingAcceptPromise = Promise.resolve(run())
        .catch(() => false)
        .finally(() => {
          this.marketingAcceptPromise = null;
        });
      return this.marketingAcceptPromise;
    }

    submitPayload(payload, decision, {
      automatic = false,
      apiOverride = null,
      transitionReason = '',
      transitionDetail = ''
    } = {}) {
      if (!LOCAL_FALLBACK_DECISIONS.includes(decision) || !this.hasValidCategories(payload)) {
        return Promise.resolve(false);
      }

      if (!automatic) {
        this.lastSubmission = {
          decision,
          categories: { ...payload },
          transitionReason
        };
        this.clearStatus();
        this.setSaving(true);
      }

      if (this.mode === RUNTIME_MODES.LOCAL_FALLBACK && !apiOverride) {
        if (automatic) return Promise.resolve(false);
        this.submitLocalFallback(payload, decision);
        return Promise.resolve(false);
      }

      if (!automatic && this.mode === RUNTIME_MODES.SHOPIFY && !this.hasUsableCustomerPrivacyApi(this.api)) {
        this.api = null;
        this.apiPromise = null;
        consentStateController.attachShopifyApi(null, 'shopify_unavailable');
        this.mode = RUNTIME_MODES.LOCAL_FALLBACK;
        this.submitLocalFallback(payload, decision);
        return Promise.resolve(false);
      }

      if (!automatic && this.mode !== RUNTIME_MODES.SHOPIFY) {
        this.setSaving(false);
        this.showError(this.dataset.submitErrorText, 'submission');
        return Promise.resolve(false);
      }

      const api = apiOverride || this.api;
      if (!this.hasUsableCustomerPrivacyApi(api)) {
        if (!automatic) {
          this.setSaving(false);
          this.showError(this.dataset.submitErrorText, 'submission');
        }
        return Promise.resolve(false);
      }
      const operation = this.startConsentOperation();
      return this.recordConsent(api, payload, operation)
        .then(({ consent }) => {
          if (!this.isCurrentConsentOperation(operation)) return false;
          this.api = api;
          this.mode = RUNTIME_MODES.SHOPIFY;
          consentStateController.syncFromShopify(
            transitionReason || this.getShopifyTransitionReason(decision, payload.marketing),
            transitionDetail
          );
          this.removeLocalFallbackConsent();
          this.sessionResolved = true;
          this.initialDisplayEvaluated = true;
          if (!automatic) this.lastSubmission = null;
          this.syncPreferencesFromCategories(consent);
          if (!automatic) this.setSaving(false);
          if (this.isOpen) this.close();
          return true;
        })
        .catch(() => {
          if (!this.isCurrentConsentOperation(operation)) return false;
          if (!automatic) {
            this.setSaving(false);
            this.showError(this.dataset.submitErrorText, 'submission');
          }
          return false;
        })
        .finally(() => {
          this.finishConsentOperation(operation);
        });
    }

    submitLocalFallback(payload, decision) {
      const fallback = this.writeLocalFallbackConsent(decision, payload);
      if (!fallback) {
        this.setSaving(false);
        this.showError(this.dataset.submitErrorText, 'submission');
        return;
      }

      this.sessionResolved = true;
      this.initialDisplayEvaluated = true;
      this.lastSubmission = null;
      this.syncPreferencesFromCategories(fallback.categories);
      this.setSaving(false);
      this.close();
    }

    recordConsent(api, payload, operation) {
      return new Promise((resolve, reject) => {
        let completed = false;
        let timeout;

        const finish = (callback, value) => {
          if (completed) return;
          completed = true;
          window.clearTimeout(timeout);
          this.consentOperationTimers.delete(timeout);
          callback(value);
        };

        timeout = window.setTimeout(() => {
          finish(reject, new Error('Consent callback timeout'));
        }, 8000);

        this.consentOperationTimers.set(timeout, () => {
          finish(reject, new Error('Consent operation cancelled'));
        });

        try {
          api.setTrackingConsent(payload, () => {
            if (completed) return;
            if (!this.isCurrentConsentOperation(operation)) {
              finish(reject, new Error('Consent operation cancelled'));
              return;
            }

            this.confirmConsent(api, payload, operation)
              .then((consent) => {
                finish(resolve, { api, consent });
              })
              .catch((error) => {
                finish(reject, error);
              });
          });
        } catch (error) {
          finish(reject, error);
        }
      });
    }

    async confirmConsent(api, payload, operation) {
      for (let attempt = 0; attempt < 4; attempt += 1) {
        if (!this.isCurrentConsentOperation(operation)) throw new Error('Consent operation cancelled');
        const state = api.currentVisitorConsent();
        const confirmed = PURPOSES.every((purpose) => {
          return this.normalizeConsent(state?.[purpose]) === payload[purpose];
        });
        if (confirmed) return state;
        await this.waitForConsentOperation(160 * (attempt + 1), operation);
      }
      throw new Error('Consent state was not confirmed');
    }

    retry() {
      if (this.isSaving || this.isSynchronizing) return;
      if (this.retryMode === 'submission' && this.lastSubmission) {
        const submission = this.lastSubmission;
        if (submission.decision === 'accept') {
          void this.performShopifyMarketingAccept({ reason: submission.transitionReason || 'accept' });
          return;
        }
        void this.submitPayload(
          { ...submission.categories },
          submission.decision,
          { transitionReason: submission.transitionReason }
        );
        return;
      }

      this.startInitialization();
    }

    setSaving(saving) {
      this.isSaving = saving;
      this.dialog.setAttribute('aria-busy', String(saving));
      this.setInteractive(!saving);
      if (saving) this.showStatus(this.dataset.savingText, 'saving');
    }

    setInteractive(enabled) {
      [...this.actionButtons, ...this.viewButtons, ...this.toggles].forEach((control) => {
        control.disabled = !enabled;
      });
    }

    showStatus(message, state) {
      this.status.textContent = message;
      this.status.dataset.state = state;
      this.status.hidden = false;
    }

    clearStatus() {
      this.status.textContent = '';
      this.status.removeAttribute('data-state');
      this.status.hidden = true;
      this.retryButton.hidden = true;
      this.retryMode = null;
    }

    showError(message, retryMode) {
      this.showStatus(message, 'error');
      this.retryMode = retryMode;
      this.retryButton.hidden = false;
    }
  }

  customElements.define('nouvac-cookie-consent', NouvacCookieConsent);

  if (IS_BOOKING_TUNNEL && !document.querySelector('nouvac-cookie-consent')) {
    const silentController = document.createElement('nouvac-cookie-consent');
    silentController.hidden = true;
    silentController.setAttribute('aria-hidden', 'true');
    silentController.setAttribute('data-nouvac-cookie-silent-controller', 'true');
    document.body.appendChild(silentController);
  }
})();
